import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Shield } from "lucide-react";

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function AdminImpersonationLogs() {
  const [sessions, setSessions] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const [s, a] = await Promise.all([
          api.get("/admin/impersonation-logs?limit=200"),
          api.get("/admin/impersonation-actions?limit=500"),
        ]);
        if (stop) return;
        setSessions(s.data.items || []);
        setActions(a.data.items || []);
      } finally { if (!stop) setLoading(false); }
    })();
    return () => { stop = true; };
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10" data-testid="impersonation-logs-page">
      <div className="border-b border-border pb-6 mb-6">
        <div className="font-overline text-accent flex items-center gap-2">
          <Shield className="w-3.5 h-3.5" /> Admin Impersonation Audit Log
        </div>
        <h1 className="font-display text-3xl mt-2">Impersonation activity</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Every impersonation session and every write performed during impersonation is logged here for compliance review.
        </p>
      </div>

      <Tabs defaultValue="sessions">
        <TabsList className="rounded-none">
          <TabsTrigger value="sessions" data-testid="imp-tab-sessions">
            Sessions ({sessions.length})
          </TabsTrigger>
          <TabsTrigger value="actions" data-testid="imp-tab-actions">
            Write actions ({actions.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sessions" className="mt-4">
          {loading ? <Skeleton /> : sessions.length === 0 ? <Empty /> : (
            <div className="border border-border overflow-x-auto" data-testid="imp-sessions-table">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 font-overline text-xs">
                  <tr>
                    <Th>Started</Th><Th>Ended</Th><Th>Admin</Th>
                    <Th>Target</Th><Th>Role</Th><Th>Reason</Th><Th>IP</Th><Th>Mode</Th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.imp_id} className="border-t border-border" data-testid={`imp-session-${s.imp_id}`}>
                      <Td>{fmt(s.started_at)}</Td>
                      <Td>{s.ended_at ? fmt(s.ended_at) : <Badge>Active</Badge>}</Td>
                      <Td className="font-mono-data text-xs">{s.admin_email}</Td>
                      <Td className="font-mono-data text-xs">{s.target_user_email}</Td>
                      <Td>{s.target_role}</Td>
                      <Td className="max-w-[200px] truncate" title={s.reason_optional || ""}>{s.reason_optional || "—"}</Td>
                      <Td className="font-mono-data text-xs">{s.ip_address || "—"}</Td>
                      <Td>{s.impersonation_mode}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="actions" className="mt-4">
          {loading ? <Skeleton /> : actions.length === 0 ? <Empty /> : (
            <div className="border border-border overflow-x-auto" data-testid="imp-actions-table">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 font-overline text-xs">
                  <tr>
                    <Th>Timestamp</Th><Th>Admin</Th><Th>Acting as</Th><Th>Role</Th>
                    <Th>Action</Th><Th>Status</Th><Th>Entity</Th>
                  </tr>
                </thead>
                <tbody>
                  {actions.map((a, i) => (
                    <tr key={i} className="border-t border-border">
                      <Td>{fmt(a.timestamp)}</Td>
                      <Td className="font-mono-data text-xs">{a.admin_email}</Td>
                      <Td className="font-mono-data text-xs">{a.impersonated_user_email}</Td>
                      <Td>{a.target_role}</Td>
                      <Td className="font-mono-data text-xs">{a.action_performed}</Td>
                      <Td>{a.http_status}</Td>
                      <Td className="font-mono-data text-xs">
                        {a.entity_type || ""}{a.entity_id ? ` / ${a.entity_id}` : ""}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-3 py-2 font-medium">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 ${className}`}>{children}</td>;
const Skeleton = () => <div className="h-32 bg-muted/30 animate-pulse" />;
const Empty = () => <div className="border border-dashed border-border p-6 text-center text-muted-foreground text-sm">No entries yet.</div>;
const Badge = ({ children }) => <span className="inline-flex items-center px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-xs uppercase tracking-wider">{children}</span>;
